import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './SettingsModal.css';

interface NotificationPrefs {
  email_enabled: boolean;
  push_enabled: boolean;
  quiet_hours_enabled: boolean;
  quiet_start: string;
  quiet_end: string;
  email_address: string;
  timezone: string;
}

interface SettingsModalProps {
  apiUrl: string;
  userToken: string | null;
  isOpen: boolean;
  onClose: () => void;
  theme: 'silver' | 'night' | 'ocean' | 'cosmos';
  setTheme: (theme: 'silver' | 'night' | 'ocean' | 'cosmos') => void;
  currentUser?: { name?: string; email?: string } | null;
  onLogin?: () => void;
  onLogout?: () => void;
  onCreateAccount?: () => void;
}

const SettingsModal: React.FC<SettingsModalProps> = ({
  apiUrl,
  userToken,
  isOpen,
  onClose,
  theme,
  setTheme,
  currentUser,
  onLogin,
  onLogout,
  onCreateAccount
}) => {
  const [activeTab, setActiveTab] = useState<'account' | 'appearance' | 'notifications'>('account');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Notification Preferences
  const [notifPrefs, setNotifPrefs] = useState<NotificationPrefs>({
    email_enabled: false,
    push_enabled: true,
    quiet_hours_enabled: false,
    quiet_start: '22:00',
    quiet_end: '07:00',
    email_address: '',
    timezone: 'UTC'
  });

  useEffect(() => {
    if (isOpen && userToken) {
      fetchNotificationPrefs();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, userToken]);

  const fetchNotificationPrefs = async () => {
    try {
      const response = await axios.get(`${apiUrl}/notifications/preferences`, {
        headers: { 'Authorization': `Bearer ${userToken}` }
      });
      if (response.data.preferences) {
        setNotifPrefs(response.data.preferences);
      }
    } catch (err) {
      console.error('Failed to fetch notification preferences:', err);
    }
  };

  const saveNotificationPrefs = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await axios.put(`${apiUrl}/notifications/preferences`, null, {
        headers: { 'Authorization': `Bearer ${userToken}` },
        params: notifPrefs
      });
      setSuccess('Settings saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const requestPushPermission = async () => {
    if (!('Notification' in window)) {
      setError('Push notifications not supported in this browser');
      return;
    }

    const permission = await Notification.requestPermission();
    if (permission === 'granted') {
      setSuccess('Push notifications enabled!');
      setNotifPrefs({ ...notifPrefs, push_enabled: true });

      // Register service worker and get subscription
      // This would require a service worker setup
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError('Push notification permission denied');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="settings-overlay" onClick={onClose}>
      <div className="settings-modal" onClick={(e) => e.stopPropagation()}>
        <div className="settings-header">
          <h2>Settings</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {/* Tabs */}
        <div className="settings-tabs">
          <button
            className={`settings-tab ${activeTab === 'account' ? 'active' : ''}`}
            onClick={() => setActiveTab('account')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
            Account
          </button>
          <button
            className={`settings-tab ${activeTab === 'appearance' ? 'active' : ''}`}
            onClick={() => setActiveTab('appearance')}
          >
            🎨 Appearance
          </button>
          <button
            className={`settings-tab ${activeTab === 'notifications' ? 'active' : ''}`}
            onClick={() => setActiveTab('notifications')}
          >
            🔔 Notifications
          </button>

        </div>

        {/* Messages */}
        {error && <div className="settings-error">{error}</div>}
        {success && <div className="settings-success">{success}</div>}

        {/* Tab Content */}
        <div className="settings-content">
          {/* Account Tab */}
          {activeTab === 'account' && (
            <div className="tab-content">
              {currentUser ? (
                <>
                  <div className="account-profile">
                    <div className="account-avatar">
                      {currentUser.name?.charAt(0).toUpperCase() || currentUser.email?.charAt(0).toUpperCase() || 'U'}
                    </div>
                    <div className="account-info">
                      <h3>{currentUser.name || 'User'}</h3>
                      <p>{currentUser.email}</p>
                    </div>
                  </div>

                  <div className="account-actions">
                    {onLogout && (
                      <button className="logout-btn-settings" onClick={() => { onLogout(); onClose(); }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                          <polyline points="16 17 21 12 16 7"/>
                          <line x1="21" y1="12" x2="9" y2="12"/>
                        </svg>
                        Log Out
                      </button>
                    )}
                  </div>
                </>
              ) : (
                <div className="account-logged-out">
                  <div className="account-icon-large">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                      <circle cx="12" cy="7" r="4"/>
                    </svg>
                  </div>
                  <h3>Welcome to Takku</h3>
                  <p>Sign in to sync your chats, memories, and preferences across devices.</p>

                  <div className="account-actions">
                    {onLogin && (
                      <button className="login-btn-settings" onClick={() => { onLogin(); onClose(); }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
                          <polyline points="10 17 15 12 10 7"/>
                          <line x1="15" y1="12" x2="3" y2="12"/>
                        </svg>
                        Log In
                      </button>
                    )}
                    {onCreateAccount && (
                      <button className="create-account-btn-settings" onClick={() => { onCreateAccount(); onClose(); }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                          <circle cx="8.5" cy="7" r="4"/>
                          <line x1="20" y1="8" x2="20" y2="14"/>
                          <line x1="23" y1="11" x2="17" y2="11"/>
                        </svg>
                        Create Account
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Appearance Tab */}
          {activeTab === 'appearance' && (
            <div className="tab-content">
              <h3>Theme</h3>
              <div className="theme-options">
                <label className={`theme-option ${theme === 'silver' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="theme"
                    value="silver"
                    checked={theme === 'silver'}
                    onChange={() => setTheme('silver')}
                  />
                  <div className="theme-preview silver">
                    <span className="theme-icon">🪞</span>
                    <span>Silver</span>
                  </div>
                </label>
                <label className={`theme-option ${theme === 'night' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="theme"
                    value="night"
                    checked={theme === 'night'}
                    onChange={() => setTheme('night')}
                  />
                  <div className="theme-preview night">
                    <span className="theme-icon">🌙</span>
                    <span>Night</span>
                  </div>
                </label>
                <label className={`theme-option ${theme === 'ocean' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="theme"
                    value="ocean"
                    checked={theme === 'ocean'}
                    onChange={() => setTheme('ocean')}
                  />
                  <div className="theme-preview ocean">
                    <span className="theme-icon">🌊</span>
                    <span>Ocean</span>
                  </div>
                </label>
                <label className={`theme-option ${theme === 'cosmos' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="theme"
                    value="cosmos"
                    checked={theme === 'cosmos'}
                    onChange={() => setTheme('cosmos')}
                  />
                  <div className="theme-preview cosmos">
                    <span className="theme-icon">🌌</span>
                    <span>Cosmos</span>
                  </div>
                </label>
              </div>
              <p className="settings-note">Choose your preferred visual theme.</p>
            </div>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <div className="tab-content">
              <h3>Push Notifications</h3>
              <div className="setting-row">
                <div className="setting-info">
                  <span className="setting-label">Enable Push Notifications</span>
                  <span className="setting-description">Get notified about reminders and events</span>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={notifPrefs.push_enabled}
                    onChange={(e) => setNotifPrefs({ ...notifPrefs, push_enabled: e.target.checked })}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              {notifPrefs.push_enabled && (
                <button className="enable-push-btn" onClick={requestPushPermission}>
                  Enable Browser Notifications
                </button>
              )}

              <h3>Quiet Hours</h3>
              <div className="setting-row">
                <div className="setting-info">
                  <span className="setting-label">Enable Quiet Hours</span>
                  <span className="setting-description">Pause notifications during specific hours</span>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={notifPrefs.quiet_hours_enabled}
                    onChange={(e) => setNotifPrefs({ ...notifPrefs, quiet_hours_enabled: e.target.checked })}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              {notifPrefs.quiet_hours_enabled && (
                <div className="quiet-hours-config">
                  <div className="time-inputs">
                    <div className="time-input-group">
                      <label>Start</label>
                      <input
                        type="time"
                        value={notifPrefs.quiet_start}
                        onChange={(e) => setNotifPrefs({ ...notifPrefs, quiet_start: e.target.value })}
                        className="time-input"
                      />
                    </div>
                    <span className="time-separator">to</span>
                    <div className="time-input-group">
                      <label>End</label>
                      <input
                        type="time"
                        value={notifPrefs.quiet_end}
                        onChange={(e) => setNotifPrefs({ ...notifPrefs, quiet_end: e.target.value })}
                        className="time-input"
                      />
                    </div>
                  </div>
                </div>
              )}

              <h3>Timezone</h3>
              <select
                value={notifPrefs.timezone}
                onChange={(e) => setNotifPrefs({ ...notifPrefs, timezone: e.target.value })}
                className="timezone-select"
              >
                <option value="UTC">UTC</option>
                <option value="America/New_York">Eastern Time (US)</option>
                <option value="America/Chicago">Central Time (US)</option>
                <option value="America/Denver">Mountain Time (US)</option>
                <option value="America/Los_Angeles">Pacific Time (US)</option>
                <option value="Europe/London">London</option>
                <option value="Europe/Paris">Paris</option>
                <option value="Asia/Tokyo">Tokyo</option>
                <option value="Asia/Kolkata">India (IST)</option>
                <option value="Australia/Sydney">Sydney</option>
              </select>

              <button
                className="save-settings-btn"
                onClick={saveNotificationPrefs}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Notification Settings'}
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default SettingsModal;
